import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import ComponentTypesMetadata from '@atlassian/jira-compass-common/src/common/ui/component-types-metadata/index.tsx';
import { getIdFromAri } from '@atlassian/jira-compass-common/src/common/utils.tsx';
import { ComponentTypeIcon } from '@atlassian/jira-compass-common/src/ui/component-type-icons/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { RelationshipNode } from '../../../../../../common/model/types.tsx';
import messages from './messages.tsx';

type Props = {
	node: RelationshipNode;
	ownerName?: string;
};

type IconType = keyof typeof ComponentTypesMetadata | 'OTHER';
function isIconType(raw: string): raw is IconType {
	return raw in ComponentTypesMetadata;
}

const toComponentType = (raw: string): IconType => (isIconType(raw) ? raw : 'OTHER');

export default function DependencyRow(props: Props) {
	const { node, ownerName } = props;
	const { formatMessage } = useIntl();
	const componentId = useMemo(() => getIdFromAri(node.id), [node.id]);

	const ownedBy = useMemo(() => {
		const componentNameComponent = (
			<>
				<Box xcss={iconWrapper}>
					<ComponentTypeIcon type={toComponentType(node.typeId)} size="small" />
				</Box>
				<Text>
					<Button
						appearance="link"
						spacing="none"
						href={`/compass/component/${componentId}`}
						target="_blank"
						rel="noopener noreferrer"
						onClick={(_, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'dependencyComponentLink');
						}}
					>
						{node.name}
					</Button>
				</Text>
				&nbsp;
			</>
		);
		if ((ownerName?.length ?? 0) > 0) {
			const ownerId = getIdFromAri(node.ownerId ?? '');

			const teamNameComponent = (
				<>
					&nbsp;
					<Button
						appearance="link"
						spacing="none"
						href={`/compass/people/team/${ownerId}`}
						target="_blank"
						rel="noopener noreferrer"
						onClick={(_, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'dependencyOwnerLink');
						}}
					>
						{ownerName}
					</Button>
				</>
			);
			return (
				<>
					{formatMessage(messages.ownedBy, {
						text: (chunks) => <Text>{chunks}</Text>,
						componentName: componentNameComponent,
						teamName: teamNameComponent,
					})}
				</>
			);
		}
		return (
			<>
				{formatMessage(messages.noOwner, {
					text: (chunks) => <Text>{chunks}</Text>,
					componentName: componentNameComponent,
				})}
			</>
		);
	}, [node.ownerId, ownerName, componentId, node.name, node.typeId, formatMessage]);

	return (
		<Row data-testid="project-directories.components.compass-table.cells.dependencies.popup-content.depency-row.row">
			{ownedBy}
		</Row>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Row = styled.div({
	display: 'flex',
	alignItems: 'center',
	width: 432,
	height: 40,
	marginBottom: token('space.100'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),

	backgroundColor: token('color.background.accent.gray.subtlest'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Text = styled.p({
	color: token('color.text.subtlest'),
	margin: 0,
});

const iconWrapper = xcss({
	marginLeft: 'space.100',
	marginRight: 'space.100',
});
